import { sortedIndexBy } from 'lodash';
import { mapAsync } from '@fullstory/util/iterators';

/**
 * Range<T> represents a `value` of type T within the range
 * [`start`-inclusive, `end`-exclusive).
 *
 * The boundaries `start` and `end` should be greater than or equal
 * to 0; typically they represent time offsets.
 */
export type Range<T> = {
  start: number;
  /**
   * Range.end is undefined for the last range in a series.
   */
  end: number | undefined;
  value: T;
};

/**
 * Piece<T> represents a mapping to a `value` T from start, numerical
 * value >= 0, to an unspecified endpoint. Sequences of Piece<T>s are
 * used by the RangeIndex classes; each Piece<T> in this context
 * corresponds to a unique Range<T>.
 */
export type Piece<T> = Pick<Range<T>, 'start' | 'value'>;

abstract class RangeIndex<V> {
  protected pieces: Piece<V>[] = [];

  get start(): number {
    return this.pieces[0]?.start ?? NaN;
  }
  private indexFor(qTime: number): number {
    // Coerce invalid time types to zero.
    const time = isNaN(qTime) || qTime < 0 ? 0 : qTime;
    const desired = { start: time };
    const pieceStarts: { start: number }[] = this.pieces;
    // TODO(bendean): Consider switching this out for an optimized binary
    // search (branchless and bitshifting):
    // https://github.com/quantified-uncertainty/squiggle/pull/1290#issuecomment-1302224197
    const i = sortedIndexBy(pieceStarts, desired, p => p.start);
    // Check if we got an exact match. (The '<=' comparison is just paranoia,
    // since floating point numbers are weird; we really only need '==='.)
    if (i < this.pieces.length && this.pieces[i]!.start <= time) {
      return i;
    }
    // We want the previous piece, or -1.
    return i - 1;
  }
  /**
   * Add a new piece to our pieces list without breaking any of the
   * constraints established in normalizePieces().
   *
   * Performs best when `p.start > this.pieces[last]`, ie, when p can
   * simply be appended.
   */
  protected add(p: Piece<V>): void {
    if (isNaN(p.start)) {
      return; // NaN is invalid. Note that Infinity is permitted, to "close" the stream.
    }
    if (p.start < 0) {
      p.start = 0;
    }

    const { pieces } = this;
    const end = pieces.length - 1;
    const last = pieces[end];
    if (!last) {
      pieces.push(p);
      return;
    }
    // The typical case: adding p to the end.
    if (p.start > last.start) {
      // A *single* duplicate is allowed at the end -- enforce that constraint.
      if (pieces.length >= 2 && last.value === pieces[end - 1]!.value) {
        pieces.length--;
      }
      pieces.push(p);
      return;
    }

    const i = this.indexFor(p.start);
    if (pieces[i]) {
      if (pieces[i]!.value === p.value) {
        return; // Prior duplicate (in contrast to "at the end" above).
      }
      if (pieces[i]!.start === p.start) {
        // Perfect overlap. Overwrite the old piece.
        pieces[i] = p;
        return;
      }
    }
    if (pieces[i + 1] && pieces[i + 1]!.value === p.value && i + 1 < pieces.length - 1) {
      // Subsequent duplicate.
      pieces[i + 1] = p;
      return;
    }
    pieces.splice(i + 1, 0, p);
  }

  private getRangeAtIndex(i: number): Range<V> | undefined {
    const piece = this.pieces[i];
    if (!piece) return undefined;
    const { start, value } = piece;
    // The `end` value is unknown if we don't yet know what the next Piece's
    // start time will be.
    const end = this.pieces[i + 1]?.start;
    return { start, end, value };
  }

  /**
   * {@returns} the Range containing {@arg queryTime}.
   * Note that negative arguments are coerced to 0.
   */
  getRange(queryTime: number): Range<V> {
    const time = Math.max(queryTime, 0);
    if (this.pieces.length === 0) {
      throw new Error(`Value at time ${time} unknown because index is empty.`);
    }
    const horizon = this.pieces[this.pieces.length - 1]!.start;
    if (time > horizon) {
      throw new Error(`Value at time ${time} unknown because >${horizon}`);
    }
    const i = this.indexFor(time);
    const range = this.getRangeAtIndex(i);
    if (!range) {
      throw new Error(`Value at time ${time} unknown because range at index ${i} is missing.`);
    }
    if (range.start > time) {
      if (i === 0) {
        // Values before the first start time are considered unknown.
        throw new Error(`Value at time ${time} unknown because <${range.start}`);
      } else {
        throw new Error(`CODE BUG: loop for time ${time} failed`);
      }
    }
    return range;
  }

  /**
   * at(time) throws when the value at time is _indeterminate_. This is
   * the case when the time has not yet been reached or is less than the
   * first piece's start value.
   *
   * For this reason, it's often a good idea to include a Piece like
   * start: 0, value: some_reasonable_default}` when constructing
   * RangeIndices.
   *
   * In general, at() is pure -- always returning the same output for
   * any given input -- as long as the order of add() calls (or the input
   * stream in the case of AsyncRangeIndex) is ordered by start time.
   */
  at(time: number): V {
    return this.getRange(time).value;
  }

  // TODO(bendean): This method is currently biased to the needs of
  // timeline.ts. We can make it a little nicer / more general:
  // - Return a RangeIndex instead of a a plain iterator
  // - Have an async version
  *slice(start = 0, end = Infinity): IterableIterator<Range<V>> {
    for (let i = Math.max(this.indexFor(start), 0); i < this.pieces.length; i++) {
      const range = this.getRangeAtIndex(i);
      if (!range || range.start >= end) {
        break;
      }
      // It is safe to modify `range`, a transient object.
      range.start = Math.max(start, range.start);
      if (range.end !== undefined) {
        range.end = range.end ?? Math.min(end, range.end);
      }
      yield range;
    }
  }

  *values(): IterableIterator<V> {
    for (const { value } of this.pieces) yield value;
  }

  protected addLastPiece() {
    if (this.pieces.length > 0) {
      const last = this.pieces[this.pieces.length - 1]!;
      this.add({ start: Infinity, value: last.value });
    }
  }
}

export class SyncRangeIndex<V> extends RangeIndex<V> {
  /**
   * Lightweight immutable, synchronous RangeIndex.
   *
   * Note that unless {@arg alreadyNormalized = true},
   * {@arg pieces} is sorted and normalized in place. This fact is
   * used by AsyncRangeIndex.
   */
  constructor(pieces: Piece<V>[], alreadyNormalized = false) {
    super();
    this.pieces = pieces;
    if (!alreadyNormalized) {
      normalizePieces(pieces);
    }
    this.addLastPiece();
  }
  *[Symbol.iterator](): IterableIterator<Piece<V>> {
    yield* this.pieces;
  }
}

/**
 * Streams of time ranges are batched up because synchronous iteration
 * is much faster than async. Concretely, a batch could represent a
 * single server response, or a group of lazily-processed events.
 */

export type PieceStream<V> = AsyncIterable<Piece<V>>;
export type BatchedPieceStream<V> = AsyncIterable<Piece<V>[]>;

export class AsyncRangeIndex<V> extends RangeIndex<V> {
  static fromStream<V>(pieces: PieceStream<V>): AsyncRangeIndex<V> {
    // TODO(bendean): Try to make this more efficient by batching up
    // `pieces` intelligently. Either
    // - According to some sampling function
    // - Using batchedSource from our iterators util package, and
    //   reworking the AsyncRangeIndex implementation to read its
    //   input lazily.
    return new AsyncRangeIndex(mapAsync(pieces, p => [p]));
  }
  static fromBatches<V>(pieces: BatchedPieceStream<V>): AsyncRangeIndex<V> {
    return new AsyncRangeIndex(pieces);
  }
  private nextProcessedBatch!: Promise<IteratorResult<Piece<V>[]>>;
  // Resolves when all input values have been processed, maybe never.
  readonly closed: Promise<void>;
  /**
   * AsyncRangeIndex reads its values by *eagerly* looping through
   * {@arg stream}. {@initPieces} can be used to pass a "default" value
   * with {start: 0}.
   */
  private constructor(batches: BatchedPieceStream<V>) {
    super();
    // Yields normalized batches AFTER adding them to this.pieces.
    const that = this;
    const processed = (async function* (): AsyncIterableIterator<Piece<V>[]> {
      for await (const batch of batches) {
        for (const piece of batch) {
          that.add(piece);
        }
        yield batch;
      }
      that.addLastPiece();
    })();

    // Now eagerly consume and process the stream, updating
    // this.nextProcessedBatch as a hook into the iteration.
    this.nextProcessedBatch = processed.next();
    this.closed = (async (): Promise<void> => {
      while (true) {
        const { done } = await this.nextProcessedBatch;
        if (done) break;
        this.nextProcessedBatch = processed.next();
      }
    })();
  }

  /**
   * Note that iterated addPieces don't have the same batch sizes as the
   * input, and can even vary between calls.
   */
  async *batches(): BatchedPieceStream<V> {
    if (this.pieces.length > 0) {
      yield [...this.pieces];
    }
    yield* this.futureBatches();
  }

  async *[Symbol.asyncIterator](): PieceStream<V> {
    for await (const batch of this.batches()) {
      yield* batch;
    }
  }

  private async *futureBatches(): BatchedPieceStream<V> {
    while (true) {
      const { value, done } = await this.nextProcessedBatch;
      if (done) break;
      yield value;
    }
  }
  /**
   * rangeWhen() and when() provide async ways to query times that haven't
   * yet been loaded.
   */
  async rangeWhen(time: number): Promise<Range<V>> {
    const last = this.pieces[this.pieces.length - 1];
    if (last && time <= last.start) {
      return this.getRange(time);
    }
    await this.nextProcessedBatch;
    return this.rangeWhen(time);
  }
  async when(time: number): Promise<V> {
    return (await this.rangeWhen(time)).value;
  }
  // Once this index's input stream has closed, it's fully defined, and
  // can be represented as a SyncRangeIndex.
  get synchronous(): Promise<SyncRangeIndex<V>> {
    return this.closed.then(() => new SyncRangeIndex(this.pieces, true));
  }
}

/**
 * Normalize {@arg pieces} in place, sorting it and removing invalid
 * and redundant values.
 *
 * Returns the largest `start` property in `pieces`.
 */
function normalizePieces(pieces: Piece<unknown>[]): void {
  // Normalize invalid start times.
  pieces.sort((a, b) => {
    if (isNaN(a.start)) return 1;
    if (isNaN(b.start)) return -1;
    return Math.sign(a.start - b.start);
  });
  // Clamp to zero.
  for (const p of pieces) {
    if (p.start < 0) p.start = 0;
    else break; // Because sorted.
  }

  // Silently remove NaN values, which are already sorted to the end.
  while (pieces.length > 0 && isNaN(pieces[pieces.length - 1]!.start)) {
    pieces.pop();
  }

  // Delete extraneous values in place: Advance pointers w and r, reading
  // from r and writing back into w.
  let [w, r] = [0, 0];
  while (r < pieces.length) {
    const duplicate = w > 0 && pieces[w - 1]!.value === pieces[r]!.value;
    const overwrite = r < pieces.length - 1 && pieces[r + 1]!.start === pieces[r]!.start;
    const shouldDelete = duplicate || overwrite;
    if (!shouldDelete) {
      pieces[w] = pieces[r]!;
      w++;
    }
    r++;
  }
  pieces.length = w;
}
